/**
 * Client wrapper around the extractListingFromPdf callable.
 *
 * Reads the File as base64, sends it to the Cloud Function, and hands back
 * the validated ListingExtraction. Kept separate from pipeline.ts because it
 * runs independently of the 4-stage PLU analysis — it's a pre-step that
 * prefills the Nouvelle form rather than part of the pipeline proper.
 */

import { httpsCallable } from "firebase/functions";
import { functions } from "./firebase";
import type { ListingExtraction } from "./types";

// Raw PDF size cap (client-side) — matches the server. 15 MB covers every
// annonce we've seen and most compromis; photo-heavy mandats may exceed it.
const MAX_PDF_BYTES = 15 * 1024 * 1024;

const callExtract = httpsCallable<
  { pdfBase64: string; filename?: string },
  ListingExtraction
>(functions, "extractListingFromPdf", {
  // Claude extraction on a 15-page compromis takes ~10-25s; give headroom.
  timeout: 180_000,
});

export class ListingExtractionError extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
  }
}

export async function extractListingFromPdf(
  file: File,
): Promise<ListingExtraction> {
  if (
    file.type !== "application/pdf" &&
    !file.name.toLowerCase().endsWith(".pdf")
  ) {
    throw new ListingExtractionError(
      "invalid-file",
      "Veuillez sélectionner un fichier PDF.",
    );
  }
  if (file.size > MAX_PDF_BYTES) {
    throw new ListingExtractionError(
      "too-large",
      `Le PDF fait ${(file.size / 1_048_576).toFixed(1)} Mo, maximum ${
        MAX_PDF_BYTES / 1_048_576
      } Mo.`,
    );
  }

  const pdfBase64 = await fileToBase64(file);
  try {
    const res = await callExtract({ pdfBase64, filename: file.name });
    return res.data;
  } catch (e) {
    const err = e as {
      code?: string;
      message?: string;
      details?: { code?: string };
    };
    const code = err.details?.code ?? err.code ?? "unknown";
    throw new ListingExtractionError(
      code,
      err.message ?? "Extraction échouée.",
    );
  }
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () =>
      reject(reader.error ?? new Error("FileReader error"));
    reader.readAsDataURL(file);
  });
}
